import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';

/**
 * Inline dialog pro rychlé vytvoření firmy do číselníku přímo z jiného
 * formuláře (např. z objednávky). Po úspěšném vytvoření zavolá
 * `onCreated(company)` s vytvořenou firmou.
 *
 * `defaultIsCustomer` určuje, zda se firma rovnou označí jako zákazník
 * (z objednávky ano — zákaznický číselník).
 */
export default function CompanyFormDialog({
  open,
  onOpenChange,
  initialName = '',
  defaultIsCustomer = true,
  onCreated,
}) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initialName,
        ico: '',
        address: '',
        city: '',
        postalCode: '',
        country: 'Česká republika',
      });
    }
    // initialName se čte při otevření; parent ho nastaví před open, takže [open] stačí
    // a nehrozí přepsání rozdělané editace, kdyby se initialName změnil za běhu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const icoValid = !form.ico || form.ico.length === 8;
  const canSave = String(form.name || '').trim().length > 0 && icoValid;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const res = await api.post('/companies', {
        name: form.name.trim(),
        ico: form.ico || '',
        address: form.address || '',
        city: form.city || '',
        postalCode: form.postalCode || '',
        country: form.country || 'Česká republika',
        isCustomer: defaultIsCustomer,
      });
      toast.success('Firma vytvořena');
      onCreated?.(res.data);
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: 'var(--ct-surface)', borderColor: 'var(--ct-border)', color: 'var(--ct-text)' }}>
        <DialogHeader>
          <DialogTitle>Nová firma do číselníku</DialogTitle>
          <DialogDescription>
            Vytvoří se zákaznická firma. Podrobnosti (pobočky, role) lze doplnit v Evidenci společností.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="ct-form-field col-span-2">
            <label className="ct-form-label">Název společnosti *</label>
            <Input
              data-testid="company-dialog-name"
              autoFocus
              value={form.name || ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-9 text-sm"
            />
          </div>
          <div className="ct-form-field col-span-2">
            <label className="ct-form-label">IČO</label>
            <Input
              value={form.ico || ''}
              onChange={(e) => setForm({ ...form, ico: e.target.value.replace(/\D/g, '').slice(0, 8) })}
              placeholder="8 číslic"
              inputMode="numeric"
              className="h-9 text-sm w-40"
            />
            {form.ico && !icoValid && (
              <p className="text-xs mt-1" style={{ color: 'var(--ct-error)' }}>
                IČO musí mít 8 číslic.
              </p>
            )}
          </div>
          <div className="ct-form-field col-span-2">
            <label className="ct-form-label">Adresa</label>
            <Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-9 text-sm" />
          </div>
          <div className="ct-form-field">
            <label className="ct-form-label">Město</label>
            <Input value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-9 text-sm" />
          </div>
          <div className="ct-form-field">
            <label className="ct-form-label">PSČ</label>
            <Input value={form.postalCode || ''} onChange={(e) => setForm({ ...form, postalCode: e.target.value })} className="h-9 text-sm" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} className="ct-btn-secondary">
            Zrušit
          </Button>
          <Button data-testid="company-dialog-save" onClick={submit} disabled={!canSave || saving} className="ct-btn-primary">
            Vytvořit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
